import { useCallback, useEffect, useMemo, useState } from "react";
import type { FrameRecords } from "../types/api";
import type { VisibleColumn } from "./columns";

// Shared per-column filter state for the in-header filter grids (ColumnFilterGrid,
// DataTable). Owns: the {colKey: selectedValues[]} map, a lazy per-frame distinct-value
// cache, the AND-combined row predicate, and a reset when the LEDGER changes.
//
// `resetKey` is what the reset keys on, NOT the frame object. Frame identity is the wrong
// discriminator: a re-finalize (a manual match, a release, a note) rebuilds the frame for
// the SAME ledger, and resetting there silently discards the filters the user is working
// under, mid-task. Callers that can tell the two apart pass a key that changes only on a
// genuinely different ledger; the default falls back to frame identity, which is the
// historic behaviour for every caller that has no better signal.
export interface ColumnFilterState {
  colFilters: Record<string, string[]>;
  setColFilter: (colKey: string, vals: string[]) => void;
  clearAll: () => void;
  nActive: number;
  distinctFor: (col: VisibleColumn) => string[];
  filterRows: (rows: FrameRecords["rows"], columns: string[]) => FrameRecords["rows"];
  /** The AND-combined row predicate itself, or null when no filter is active. Lets a
   *  caller fold filtering into its own single pass instead of materialising a second
   *  array and then re-deriving which rows survived. */
  rowPredicate: (columns: string[]) => ((row: unknown[]) => boolean) | null;
}

export function useColumnFilters(frame: FrameRecords, resetKey?: string): ColumnFilterState {
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({});

  const identity = resetKey ?? frame;
  useEffect(() => { setColFilters({}); }, [identity]);

  // The distinct-value cache still keys on the FRAME, not on `identity`: its contents are
  // derived from these exact rows, so a re-finalize must invalidate it even when the
  // filters correctly survive.
  const distinctCache = useMemo(() => new Map<string, string[]>(), [frame]);
  const distinctFor = useCallback((col: VisibleColumn): string[] => {
    let d = distinctCache.get(col.key);
    if (!d) {
      const set = new Set<string>();
      for (const r of frame.rows) {
        const v = r[col.index];
        if (v !== null && v !== undefined && String(v).trim() !== "") set.add(String(v));
      }
      d = [...set].sort();
      distinctCache.set(col.key, d);
    }
    return d;
  }, [frame, distinctCache]);

  const setColFilter = useCallback((colKey: string, vals: string[]) =>
    setColFilters((f) => ({ ...f, [colKey]: vals })), []);
  const clearAll = useCallback(() => setColFilters({}), []);
  const nActive = useMemo(
    () => Object.values(colFilters).filter((v) => v.length > 0).length, [colFilters]);

  const rowPredicate = useCallback((columns: string[]) => {
    if (nActive === 0) return null;
    const active = Object.entries(colFilters).filter(([, v]) => v.length > 0)
      .map(([col, chosen]) => ({ i: columns.indexOf(col), set: new Set(chosen) }))
      .filter((x) => x.i >= 0);
    if (active.length === 0) return null;
    return (r: unknown[]) => active.every((a) => a.set.has(String(r[a.i] ?? "")));
  }, [colFilters, nActive]);

  const filterRows = useCallback((rows: FrameRecords["rows"], columns: string[]) => {
    const keep = rowPredicate(columns);
    return keep ? rows.filter(keep) : rows;
  }, [rowPredicate]);

  // MEMOISED AS A WHOLE, not just field by field. Both grids list this object in the
  // dependency array of the useMemo that filters their rows, so returning a fresh literal
  // meant that memo never hit and every render re-scanned the frame — and re-hashed every
  // row id in useCellSelection's stale-guard on top. Nothing about the VALUES changes;
  // only their identity is now stable between renders that changed none of them.
  return useMemo(
    () => ({ colFilters, setColFilter, clearAll, nActive, distinctFor, filterRows, rowPredicate }),
    [colFilters, setColFilter, clearAll, nActive, distinctFor, filterRows, rowPredicate]);
}
